<template>
  <div class="ir-modern-page m-left-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="rgba(255, 255, 255, 0.85)"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    
    <!-- Modern Notification Snackbar -->
    <v-snackbar v-model="showsnackbar" :color="snackbarColor || '#1e293b'" right top :timeout="timeout" elevation="8" class="modern-snackbar">
      <div class="d-flex align-center justify-space-between w-100 py-1">
        <div class="d-flex align-center gap-2">
          <v-icon color="#ffffff" size="18">mdi-information-outline</v-icon>
          <span class="snackbar-text font-weight-medium white--text">{{ msg }}</span>
        </div>
        <v-btn icon small @click="showsnackbar = false" class="ml-2">
          <v-icon color="#ffffff" size="16">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Compact Header Row -->
    <div class="d-flex align-center justify-space-between flex-wrap gap-2 mb-3 pt-0">
      <div class="d-flex align-center flex-wrap gap-2">
        <v-btn
          icon
          small
          class="back-btn mr-1"
          @click="cancel()"
          title="Back"
        >
          <v-icon size="18" color="#475569">mdi-arrow-left</v-icon>
        </v-btn>
        <PageBreadcrumb :items="[{ text: 'Purchase', to: '/PurchaseList' }, { text: 'Issue & Receipt', to: '/IssueReceiptList' }, { text: 'New Voucher' }]" />
        <span v-if="voucherNo" class="voucher-badge">
          <v-icon x-small color="#9a6b27" class="mr-1">mdi-receipt-text-outline</v-icon>
          #{{ prefix ? prefix + '-' : '' }}{{ voucherNo }}
        </span>
        <span :class="['type-pill', type === 'Issue' ? 'type-issue' : 'type-receipt']">
          <span class="type-dot"></span>
          {{ type }}
        </span>
        <span class="payment-mode-pill">
          <v-icon x-small :color="paymentMode === 'Cash' ? '#16a34a' : '#2563eb'" class="mr-1">
            {{ paymentMode === 'Cash' ? 'mdi-cash' : 'mdi-bank' }}
          </v-icon>
          {{ paymentMode }}
        </span>
      </div>

      <div class="d-flex align-center gap-2">
        <v-btn
          outlined
          depressed
          small
          class="btn-compact action-outline-btn"
          @click="$router.push('/IssueReceiptList')"
        >
          <v-icon left x-small class="action-btn-icon">mdi-format-list-bulleted</v-icon>
          Voucher List
        </v-btn>
      </div>
    </div>

    <!-- Section 1: Voucher & Supplier Details Card -->
    <v-card class="section-card mb-4" elevation="0">
      <div class="section-header d-flex align-center justify-space-between pa-4 px-5">
        <div class="d-flex align-center gap-3">
          <div class="header-icon-circle bg-amber-soft">
            <v-icon color="#9a6b27" size="18">mdi-file-document-edit-outline</v-icon>
          </div>
          <div>
            <h2 class="section-title">Voucher &amp; Supplier Information</h2>
            <div class="section-subtitle">Core voucher details, dates, transaction type, and supplier ledger</div>
          </div>
        </div>
      </div>

      <div class="pa-5">
        <v-layout wrap class="mx-n2">
          <!-- Voucher No -->
          <v-flex xs6 sm3 md2 class="pa-2 text-left">
            <div class="field-header-row">
              <label class="form-label-elegant mb-0">Voucher No.</label>
            </div>
            <v-text-field
              v-model="voucherNo"
              outlined
              dense
              disabled
              hide-details
              class="custom-field disabled-field"
              prepend-inner-icon="mdi-lock-outline"
            ></v-text-field>
          </v-flex>

          <!-- Prefix -->
          <v-flex xs6 sm3 md2 class="pa-2 text-left">
            <div class="field-header-row">
              <label class="form-label-elegant mb-0">Prefix</label>
            </div>
            <v-text-field
              v-model="prefix"
              color="#9a6b27"
              placeholder="e.g. IR"
              outlined
              dense
              hide-details
              class="custom-field"
            ></v-text-field>
          </v-flex>

          <!-- Date -->
          <v-flex xs12 sm3 md2 class="pa-2 text-left">
            <div class="field-header-row">
              <label class="form-label-elegant mb-0">Voucher Date</label>
            </div>
            <v-menu
              ref="dateMenu"
              v-model="dateMenu"
              :close-on-content-click="false"
              :return-value.sync="date"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="date"
                  outlined
                  dense
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  color="#9a6b27"
                  class="custom-field"
                  prepend-inner-icon="mdi-calendar-month-outline"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="date"
                no-title
                scrollable
                color="#9a6b27"
                @change="$refs.dateMenu.save(date)"
              ></v-date-picker>
            </v-menu>
          </v-flex>

          <!-- Issue / Receipt Type -->
          <v-flex xs6 sm3 md3 class="pa-2 text-left">
            <div class="field-header-row">
              <label class="form-label-elegant mb-0">Voucher Type</label>
            </div>
            <v-select
              v-model="type"
              :items="['Issue', 'Receipt']"
              outlined
              dense
              hide-details
              color="#9a6b27"
              class="custom-field"
            >
              <template v-slot:selection="{ item }">
                <span class="d-flex align-center gap-1 font-weight-bold" :class="item === 'Issue' ? 'amber--text text--darken-3' : 'emerald--text'">
                  <v-icon x-small :color="item === 'Issue' ? '#b45309' : '#16a34a'">
                    {{ item === 'Issue' ? 'mdi-arrow-up-bold-circle' : 'mdi-arrow-down-bold-circle' }}
                  </v-icon>
                  {{ item }}
                </span>
              </template>
            </v-select>
          </v-flex>

          <!-- Cash / Bank -->
          <v-flex xs6 sm3 md3 class="pa-2 text-left">
            <div class="field-header-row">
              <label class="form-label-elegant mb-0">Cash / Bank</label>
            </div>
            <v-select
              v-model="paymentMode"
              :items="['Cash', 'Bank']"
              outlined
              dense
              hide-details
              color="#9a6b27"
              class="custom-field"
            >
              <template v-slot:selection="{ item }">
                <span class="d-flex align-center gap-1 font-weight-medium">
                  <v-icon x-small :color="item === 'Cash' ? '#16a34a' : '#2563eb'">
                    {{ item === 'Cash' ? 'mdi-cash' : 'mdi-bank' }}
                  </v-icon>
                  {{ item }}
                </span>
              </template>
            </v-select>
          </v-flex>

          <!-- Supplier Dropdown -->
          <v-flex xs12 sm5 md4 class="pa-2 text-left">
            <div class="field-header-row">
              <label class="form-label-elegant mb-0">Supplier <span class="required-star">*</span></label>
            </div>
            <v-autocomplete
              v-model="supplierId"
              :items="supplierList"
              item-text="supplierName"
              item-value="_id"
              outlined
              dense
              hide-details
              color="#9a6b27"
              placeholder="Search or select supplier"
              class="custom-field"
              prepend-inner-icon="mdi-account-tie-outline"
              @change="getSupplierBalance()"
            >
              <template v-slot:item="{ item }">
                <div class="d-flex align-center justify-space-between w-100 py-1">
                  <div>
                    <div class="font-weight-bold text-dark">{{ item.supplierName }}</div>
                    <div class="text-caption text-muted" v-if="item.phone">{{ item.phone }}</div>
                  </div>
                  <v-chip x-small color="#fff7ed" text-color="#c2410c" class="font-weight-medium" v-if="item.totalPendingGold">
                    {{ item.totalPendingGold }} gm
                  </v-chip>
                </div>
              </template>
            </v-autocomplete>
          </v-flex>

          <!-- Invoice Number -->
          <v-flex xs6 sm3 md2 class="pa-2 text-left">
            <div class="field-header-row">
              <label class="form-label-elegant mb-0">Invoice #</label>
              <span class="invoice-auto-tag" v-if="!invoiceNumber">Auto</span>
            </div>
            <v-text-field
              v-model="invoiceNumber"
              color="#9a6b27"
              :placeholder="nextInvoiceNumber || 'Auto-generated on save'"
              outlined
              dense
              hide-details
              class="custom-field"
            ></v-text-field>
            <div class="invoice-hint">
              {{ invoiceNumber ? 'Using this number' : 'Leave blank to auto-number, or type the supplier\'s invoice #' }}
            </div>
          </v-flex>

          <!-- Invoice Date -->
          <v-flex xs6 sm4 md2 class="pa-2 text-left">
            <div class="field-header-row">
              <label class="form-label-elegant mb-0">Invoice Date</label>
            </div>
            <v-menu
              ref="invMenu"
              v-model="invMenu"
              :close-on-content-click="false"
              :return-value.sync="invoiceDate"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="invoiceDate"
                  outlined
                  dense
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  color="#9a6b27"
                  class="custom-field"
                  prepend-inner-icon="mdi-calendar-clock-outline"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="invoiceDate"
                no-title
                scrollable
                color="#9a6b27"
                @change="$refs.invMenu.save(invoiceDate)"
              ></v-date-picker>
            </v-menu>
          </v-flex>

          <!-- Supplier Standing Balance Widgets -->
          <v-flex xs12 sm12 md4 class="pa-2 text-left" v-if="supplierId">
            <div class="field-header-row">
              <label class="form-label-elegant mb-0">Supplier Current Ledger</label>
            </div>
            <div class="balance-widgets-container">
              <div class="balance-widget">
                <div class="balance-widget-icon bg-emerald-soft">
                  <v-icon size="16" color="#059669">mdi-currency-inr</v-icon>
                </div>
                <div class="balance-widget-body">
                  <span class="balance-widget-label">Balance Amount</span>
                  <span class="balance-widget-value" :class="balanceAmountAtEntry < 0 ? 'text-danger' : 'text-dark'">
                    ₹ {{ formatNumber(balanceAmountAtEntry) }}
                  </span>
                </div>
              </div>
              <div class="balance-widget">
                <div class="balance-widget-icon bg-amber-soft">
                  <v-icon size="16" color="#d97706">mdi-scale-balance</v-icon>
                </div>
                <div class="balance-widget-body">
                  <span class="balance-widget-label">Balance Weight</span>
                  <span class="balance-widget-value text-amber-dark">
                    {{ Number(balanceWeightAtEntry || 0).toFixed(3) }} <span class="unit">gm</span>
                  </span>
                </div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </div>
    </v-card>

    <!------------------- SECTION: SETTLE EXISTING PURCHASE BILL ------------------->
    <v-card class="section-card mb-4" elevation="0" v-if="purchaseId">
      <div class="section-header d-flex align-center justify-space-between pa-4 px-5">
        <div class="d-flex align-center gap-3">
          <div class="header-icon-circle bg-blue-soft">
            <v-icon color="#2563eb" size="18">mdi-invoice-check-outline</v-icon>
          </div>
          <div>
            <div class="d-flex align-center gap-2">
              <h2 class="section-title">Settling Purchase Bill: {{ (purchaseData.billPrefix || '') + (purchaseData.billNo || billNo || purchaseId) }}</h2>
              <span class="bill-badge">Purchase Bill</span>
            </div>
            <div class="section-subtitle">Select the purchased items to settle in this voucher</div>
          </div>
        </div>

        <div class="d-flex align-center gap-2" v-if="unsettledItemsCount > 0">
          <v-btn
            x-small
            outlined
            depressed
            color="#9a6b27"
            class="action-pill-btn"
            @click="toggleSelectAllPurchaseItems()"
          >
            <v-icon left x-small>{{ isAllPurchaseItemsSelected ? 'mdi-checkbox-blank-outline' : 'mdi-checkbox-marked-outline' }}</v-icon>
            {{ isAllPurchaseItemsSelected ? 'Deselect All' : 'Select All' }}
          </v-btn>
        </div>
      </div>

      <div class="pa-0">
        <div v-if="purchaseItems.length > 0" class="modern-table-wrapper">
          <table class="modern-table">
            <thead>
              <tr>
                <th style="width: 44px" class="text-center">
                  <v-simple-checkbox
                    :value="isAllPurchaseItemsSelected"
                    :indeterminate="isPartiallySelected"
                    color="#9a6b27"
                    @input="toggleSelectAllPurchaseItems"
                  ></v-simple-checkbox>
                </th>
                <th>Item / Design</th>
                <th class="text-right">Net Weight (gm)</th>
                <th class="text-right">To Be Paid Thankam (gm)</th>
                <th class="text-center">Settle Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(value, i) in purchaseItems"
                :key="i"
                :class="['table-row', selectedPurchaseItemIds.includes(value._id) ? 'row-selected' : '']"
                @click="toggleSingleItem(value)"
              >
                <td class="text-center" @click.stop>
                  <v-checkbox
                    v-model="selectedPurchaseItemIds"
                    :value="value._id"
                    :disabled="value.settleStatus === 'Settled'"
                    color="#9a6b27"
                    hide-details
                    class="ma-0 pa-0 justify-center"
                  ></v-checkbox>
                </td>
                <td>
                  <div class="d-flex align-center gap-2">
                    <div class="item-avatar-circle">
                      <v-icon x-small color="#9a6b27">mdi-gold</v-icon>
                    </div>
                    <div>
                      <div class="font-weight-bold text-dark">
                        {{ value.designId ? value.designId.name : (value.itemName || 'Jewelry Item') }}
                      </div>
                      <div class="text-caption text-muted" v-if="value.designId && value.designId.code">
                        Code: {{ value.designId.code }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="text-right font-weight-medium">
                  {{ Number(value.netWeight || 0).toFixed(3) }} <span class="unit">gm</span>
                </td>
                <td class="text-right font-weight-bold text-amber-dark">
                  {{ Number(getItemThankamWeight(value)).toFixed(3) }} <span class="unit">gm</span>
                </td>
                <td class="text-center">
                  <span :class="['status-pill', value.settleStatus === 'Settled' ? 'status-settled' : 'status-unsettled']">
                    <span class="status-dot" :class="value.settleStatus === 'Settled' ? 'dot-settled' : 'dot-unsettled'"></span>
                    {{ value.settleStatus || 'Unsettled' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Settle Selection Summary Strip -->
          <div class="table-summary-strip d-flex align-center justify-space-between flex-wrap gap-3 pa-3 px-5">
            <div class="d-flex align-center gap-2 text-muted font-weight-medium text-caption">
              <v-icon small color="#64748b">mdi-check-circle-outline</v-icon>
              <span>{{ selectedPurchaseItemIds.length }} of {{ purchaseItems.length }} items selected for settlement</span>
            </div>
            <div class="d-flex align-center gap-3">
              <div class="summary-stat-badge">
                <span class="summary-stat-label">Total Thankam Weight:</span>
                <span class="summary-stat-val text-amber-dark">{{ selectedThankamWeightTotal.toFixed(3) }} gm</span>
              </div>
              <div class="summary-stat-badge badge-emerald-soft" v-if="thankamRate > 0">
                <span class="summary-stat-label">Total Cash Equivalent:</span>
                <span class="summary-stat-val text-emerald-dark font-weight-bold">₹ {{ formatNumber(requiredCashAmount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="empty-state-card pa-8 text-center">
          <div class="empty-icon-circle mb-3">
            <v-icon size="32" color="#9a6b27">mdi-check-decagram-outline</v-icon>
          </div>
          <div class="empty-title">All Items Settled</div>
          <div class="empty-subtitle">There are no pending unsettled items on this purchase bill.</div>
        </div>
      </div>
    </v-card>

    <!------------------- SECTION: ITEM MOVEMENT BUILDER (when !purchaseId) ------------------->
    <v-card class="section-card mb-4" elevation="0" v-if="!purchaseId">
      <div class="section-header d-flex align-center justify-space-between pa-4 px-5">
        <div class="d-flex align-center gap-3">
          <div class="header-icon-circle bg-amber-soft">
            <v-icon color="#9a6b27" size="18">mdi-cube-outline</v-icon>
          </div>
          <div>
            <h2 class="section-title">Item Movement Builder</h2>
            <div class="section-subtitle">Specify individual items, weights, purity touch, and stone values</div>
          </div>
        </div>
      </div>

      <div class="pa-5">
        <!-- Item Input Form Grid -->
        <div class="builder-grid-container pa-4 mb-4">
          <v-layout wrap class="mx-n2">
            <!-- Item Autocomplete -->
            <v-flex xs12 sm6 md4 class="pa-2 text-left">
              <label class="form-label-elegant">Item Code / Name <span class="required-star">*</span></label>
              <v-autocomplete
                v-model="curItem.selectedItem"
                :items="itemList"
                item-value="_id"
                return-object
                outlined
                dense
                hide-details
                color="#9a6b27"
                class="custom-field"
                placeholder="Search item code or name"
                @change="selectItem"
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center justify-space-between w-100 py-1">
                    <span class="font-weight-bold text-dark">{{ item.name }}</span>
                    <span class="code-pill">{{ item.code || '-' }}</span>
                  </div>
                </template>
                <template v-slot:selection="{ item }">
                  {{ item.code ? item.code + ' - ' : '' }}{{ item.name }}
                </template>
              </v-autocomplete>
            </v-flex>

            <!-- Quantity -->
            <v-flex xs6 sm3 md1 class="pa-2 text-left">
              <label class="form-label-elegant">Qty</label>
              <v-text-field
                v-model="curItem.quantity"
                type="number"
                outlined
                dense
                hide-details
                color="#9a6b27"
                class="custom-field"
              ></v-text-field>
            </v-flex>

            <!-- Category -->
            <v-flex xs6 sm3 md2 class="pa-2 text-left">
              <label class="form-label-elegant">Category</label>
              <v-select
                v-model="curItem.category"
                :items="['Gold', 'Silver', 'Diamond']"
                outlined
                dense
                hide-details
                color="#9a6b27"
                class="custom-field"
              ></v-select>
            </v-flex>

            <!-- Gross Wt -->
            <v-flex xs6 sm3 md1 class="pa-2 text-left">
              <label class="form-label-elegant">Gross Wt</label>
              <v-text-field
                v-model="curItem.grossWeight"
                type="number"
                placeholder="0.00"
                outlined
                dense
                hide-details
                color="#9a6b27"
                class="custom-field"
              ></v-text-field>
            </v-flex>

            <!-- Stone Wt -->
            <v-flex xs6 sm3 md1 class="pa-2 text-left">
              <label class="form-label-elegant">Stone Wt</label>
              <v-text-field
                v-model="curItem.stoneWeight"
                type="number"
                placeholder="0.00"
                outlined
                dense
                hide-details
                color="#9a6b27"
                class="custom-field"
              ></v-text-field>
            </v-flex>

            <!-- Cent (for diamond) -->
            <v-flex xs6 sm3 md1 class="pa-2 text-left" v-if="curItem.category === 'Diamond'">
              <label class="form-label-elegant">Cent</label>
              <v-text-field
                v-model="curItem.cent"
                type="number"
                outlined
                dense
                hide-details
                color="#9a6b27"
                class="custom-field"
              ></v-text-field>
            </v-flex>

            <!-- Stone Price -->
            <v-flex xs6 sm3 md1 class="pa-2 text-left">
              <label class="form-label-elegant">Stone Price</label>
              <v-text-field
                v-model="curItem.stonePrice"
                type="number"
                placeholder="0"
                outlined
                dense
                hide-details
                color="#9a6b27"
                class="custom-field"
              ></v-text-field>
            </v-flex>

            <!-- Touch -->
            <v-flex xs6 sm3 md1 class="pa-2 text-left">
              <label class="form-label-elegant">Touch (%)</label>
              <v-text-field
                v-model="curItem.touch"
                type="number"
                placeholder="91.6"
                outlined
                dense
                hide-details
                color="#9a6b27"
                class="custom-field"
              ></v-text-field>
            </v-flex>

            <!-- Making Charge -->
            <v-flex xs6 sm3 md1 class="pa-2 text-left">
              <label class="form-label-elegant">Making (₹)</label>
              <v-text-field
                v-model="curItem.makingCharge"
                type="number"
                placeholder="0"
                outlined
                dense
                hide-details
                color="#9a6b27"
                class="custom-field"
              ></v-text-field>
            </v-flex>

            <!-- Tax -->
            <v-flex xs6 sm3 md2 class="pa-2 text-left">
              <label class="form-label-elegant">Tax</label>
              <v-select
                v-model="curItem.taxType"
                :items="['GST', 'None']"
                outlined
                dense
                hide-details
                color="#9a6b27"
                class="custom-field"
              ></v-select>
            </v-flex>

            <!-- Add Button -->
            <v-flex xs12 sm3 md2 class="pa-2 align-self-end">
              <v-btn
                color="#9a6b27"
                dark
                depressed
                block
                height="40"
                class="btn-primary-gradient font-weight-bold"
                @click="addItem()"
              >
                <v-icon left size="16">mdi-plus-circle-outline</v-icon>
                Add Item
              </v-btn>
            </v-flex>
          </v-layout>
        </div>

        <!-- Added Items Table -->
        <div v-if="items.length > 0" class="modern-table-wrapper">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Item Code</th>
                <th>Item Name</th>
                <th>Category</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Gross Wt</th>
                <th class="text-right">Stone Wt</th>
                <th class="text-right">Net Wt</th>
                <th class="text-right">Touch (%)</th>
                <th class="text-right">Amount (₹)</th>
                <th class="text-center" style="width: 60px">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, i) in items" :key="i" class="table-row">
                <td><span class="code-pill">{{ value.itemCode || '-' }}</span></td>
                <td class="font-weight-bold text-dark">{{ value.itemName }}</td>
                <td>
                  <span class="category-pill" :class="'cat-' + (value.category || 'gold').toLowerCase()">
                    {{ value.category }}
                  </span>
                </td>
                <td class="text-right font-weight-medium">{{ value.quantity }}</td>
                <td class="text-right">{{ Number(value.grossWeight || 0).toFixed(3) }}</td>
                <td class="text-right text-muted">{{ Number(value.stoneWeight || 0).toFixed(3) }}</td>
                <td class="text-right font-weight-bold text-dark">
                  {{ (Number(value.grossWeight || 0) - Number(value.stoneWeight || 0)).toFixed(3) }}
                </td>
                <td class="text-right font-weight-medium">{{ value.touch }}%</td>
                <td class="text-right font-weight-bold text-amber-dark">
                  ₹ {{ formatNumber(value.itemTotalAmount) }}
                </td>
                <td class="text-center">
                  <v-btn icon small class="action-icon-btn delete-action-btn" @click="items.splice(i, 1)">
                    <v-icon size="16" color="#ef4444">mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Items Summary Strip -->
          <div class="table-summary-strip d-flex align-center justify-space-between flex-wrap gap-3 pa-3 px-5">
            <div class="text-muted font-weight-medium text-caption">
              Total {{ items.length }} line items in voucher
            </div>
            <div class="d-flex align-center gap-3">
              <div class="summary-stat-badge">
                <span class="summary-stat-label">Total Net Weight:</span>
                <span class="summary-stat-val text-dark">{{ itemsTotalNetWeight.toFixed(3) }} gm</span>
              </div>
              <div class="summary-stat-badge">
                <span class="summary-stat-label">Total Amount:</span>
                <span class="summary-stat-val text-amber-dark">₹ {{ formatNumber(itemsTotalAmount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="empty-state-card pa-6 text-center">
          <div class="empty-icon-circle mb-2">
            <v-icon size="26" color="#94a3b8">mdi-tray-plus</v-icon>
          </div>
          <div class="empty-title">No items added yet</div>
          <div class="empty-subtitle">Use the item builder above to add items to this voucher.</div>
        </div>
      </div>
    </v-card>

    <!------------------- SECTION: SETTLEMENT CONFIGURATION ------------------->
    <v-card class="section-card mb-4" elevation="0">
      <div class="section-header d-flex align-center justify-space-between pa-4 px-5">
        <div class="d-flex align-center gap-3">
          <div class="header-icon-circle bg-emerald-soft">
            <v-icon color="#059669" size="18">mdi-scale-balance</v-icon>
          </div>
          <div>
            <h2 class="section-title">Settlement Method</h2>
            <div class="section-subtitle">Choose how to balance the ledger: pure gold weight, cash payment, or cash-for-weight conversion</div>
          </div>
        </div>
      </div>

      <div class="pa-5">
        <!-- Settlement Mode Selector Cards -->
        <div class="settlement-modes-container mb-4">
          <div
            :class="['settlement-mode-card', settlementMode === 'Weight' ? 'mode-active' : '']"
            @click="settlementMode = 'Weight'"
          >
            <div class="mode-radio-circle">
              <v-icon x-small v-if="settlementMode === 'Weight'" color="#ffffff">mdi-check</v-icon>
            </div>
            <div class="mode-icon bg-amber-soft">
              <v-icon size="20" color="#9a6b27">mdi-gold</v-icon>
            </div>
            <div class="mode-info">
              <div class="mode-title">Weight Settlement</div>
              <div class="mode-desc">Settle balance with pure Thankam gold weight</div>
            </div>
          </div>

          <div
            :class="['settlement-mode-card', settlementMode === 'Cash' ? 'mode-active' : '']"
            @click="settlementMode = 'Cash'"
          >
            <div class="mode-radio-circle">
              <v-icon x-small v-if="settlementMode === 'Cash'" color="#ffffff">mdi-check</v-icon>
            </div>
            <div class="mode-icon bg-emerald-soft">
              <v-icon size="20" color="#059669">mdi-cash-multiple</v-icon>
            </div>
            <div class="mode-info">
              <div class="mode-title">Cash Settlement</div>
              <div class="mode-desc">Settle balance with direct cash / bank payment</div>
            </div>
          </div>

          <div
            :class="['settlement-mode-card', settlementMode === 'PayInsteadOfWeight' ? 'mode-active' : '']"
            @click="settlementMode = 'PayInsteadOfWeight'"
          >
            <div class="mode-radio-circle">
              <v-icon x-small v-if="settlementMode === 'PayInsteadOfWeight'" color="#ffffff">mdi-check</v-icon>
            </div>
            <div class="mode-icon bg-blue-soft">
              <v-icon size="20" color="#2563eb">mdi-swap-horizontal-bold</v-icon>
            </div>
            <div class="mode-info">
              <div class="mode-title">Pay Instead of Weight</div>
              <div class="mode-desc">Pay cash equivalent for pending gold weight balance</div>
            </div>
          </div>
        </div>

        <!-- Mode-Specific Input Fields -->
        <div class="settlement-fields-box pa-4 rounded-xl">
          <v-layout wrap class="mx-n2">
            <!-- Mode 1: WEIGHT -->
            <template v-if="settlementMode === 'Weight'">
              <v-flex xs12 sm6 md3 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Thankam Rate (₹)</label>
                  <span class="live-rate-tag" @click="useLiveThankamRate()" title="Click to use live rate">
                    <v-icon x-small color="#9a6b27" class="mr-1">mdi-flash</v-icon>Live Rate
                  </span>
                </div>
                <v-text-field
                  v-model="thankamRate"
                  type="number"
                  placeholder="0.00"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                  prefix="₹"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md3 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Paid Thankam Wt <span class="required-star">*</span></label>
                  <span
                    v-if="purchaseId && selectedThankamWeightTotal > 0"
                    class="fill-full-tag text-amber"
                    @click="paidThankam = selectedThankamWeightTotal.toFixed(3)"
                    title="Click to fill exact required weight"
                  >
                    <v-icon x-small color="#9a6b27" class="mr-1">mdi-check-circle-outline</v-icon>
                    Req: {{ selectedThankamWeightTotal.toFixed(3) }} gm
                  </span>
                </div>
                <v-text-field
                  v-model="paidThankam"
                  type="number"
                  placeholder="0.000"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field font-weight-bold"
                  suffix="gm"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md3 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Additional Cash (₹)</label>
                </div>
                <v-text-field
                  v-model="payAmount"
                  type="number"
                  placeholder="0.00"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                  prefix="₹"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md3 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Other Charges (₹)</label>
                </div>
                <v-text-field
                  v-model="otherCharges"
                  type="number"
                  placeholder="0.00"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                  prefix="₹"
                ></v-text-field>
              </v-flex>
            </template>

            <!-- Mode 2: CASH (5 fields cleanly filling a 12-column grid: 2 + 3 + 2 + 2 + 3 = 12) -->
            <template v-if="settlementMode === 'Cash'">
              <v-flex xs12 sm6 md2 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Payment Action</label>
                </div>
                <v-select
                  v-model="cashPayment.cashPayOrCollect"
                  :items="['Pay', 'Collect']"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                ></v-select>
              </v-flex>

              <v-flex xs12 sm6 md3 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Cash Amount (₹) <span class="required-star">*</span></label>
                  <span
                    v-if="purchaseId && requiredCashAmount > 0"
                    class="fill-full-tag text-emerald"
                    @click="cashPayment.amount = requiredCashAmount.toFixed(2)"
                    title="Click to fill exact required cash amount"
                  >
                    <v-icon x-small color="#047857" class="mr-1">mdi-check-circle-outline</v-icon>
                    Req: ₹ {{ formatNumber(requiredCashAmount) }}
                  </span>
                </div>
                <v-text-field
                  v-model="cashPayment.amount"
                  type="number"
                  placeholder="0.00"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field font-weight-bold"
                  prefix="₹"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm4 md2 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Discount (₹)</label>
                </div>
                <v-text-field
                  v-model="cashPayment.discount"
                  type="number"
                  placeholder="0.00"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                  prefix="₹"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm4 md2 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Other Charges (₹)</label>
                </div>
                <v-text-field
                  v-model="otherCharges"
                  type="number"
                  placeholder="0.00"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                  prefix="₹"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm4 md3 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Net Payable Amount (₹)</label>
                </div>
                <v-text-field
                  :value="netPayableCashAmount"
                  outlined
                  dense
                  disabled
                  hide-details
                  class="custom-field disabled-field font-weight-bold"
                  prefix="₹"
                ></v-text-field>
              </v-flex>
            </template>

            <!-- Mode 3: PAY INSTEAD OF WEIGHT (Row 1: 4 equal cols [md3], Row 2: 3 equal cols [md4]) -->
            <template v-if="settlementMode === 'PayInsteadOfWeight'">
              <v-flex xs12 sm6 md3 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Cash Action</label>
                </div>
                <v-select
                  v-model="payInsteadOfWeight.cashPayOrCollect"
                  :items="['Pay', 'Collect']"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                ></v-select>
              </v-flex>

              <v-flex xs12 sm6 md3 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Purity Touch (%)</label>
                </div>
                <v-text-field
                  v-model="payInsteadOfWeight.cashTouch"
                  type="number"
                  placeholder="91.6"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                  suffix="%"
                  @input="calcPayInsteadOfWeight()"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md3 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Rate per Gram (₹)</label>
                </div>
                <v-text-field
                  v-model="payInsteadOfWeight.ratePerGram"
                  type="number"
                  placeholder="0.00"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                  prefix="₹"
                  @input="calcPayInsteadOfWeight()"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm6 md3 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Cash Amount (₹) <span class="required-star">*</span></label>
                  <span
                    v-if="purchaseId && requiredCashAmount > 0"
                    class="fill-full-tag text-emerald"
                    @click="payInsteadOfWeight.amount = requiredCashAmount.toFixed(2); calcPayInsteadOfWeight()"
                    title="Click to fill exact required cash amount"
                  >
                    <v-icon x-small color="#047857" class="mr-1">mdi-check-circle-outline</v-icon>
                    Req: ₹ {{ formatNumber(requiredCashAmount) }}
                  </span>
                </div>
                <v-text-field
                  v-model="payInsteadOfWeight.amount"
                  type="number"
                  placeholder="0.00"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field font-weight-bold"
                  prefix="₹"
                  @input="calcPayInsteadOfWeight()"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm4 md4 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Discount (₹)</label>
                </div>
                <v-text-field
                  v-model="payInsteadOfWeight.discount"
                  type="number"
                  placeholder="0.00"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                  prefix="₹"
                  @input="calcPayInsteadOfWeight()"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm4 md4 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Other Charges (₹)</label>
                </div>
                <v-text-field
                  v-model="otherCharges"
                  type="number"
                  placeholder="0.00"
                  outlined
                  dense
                  hide-details
                  color="#9a6b27"
                  class="custom-field"
                  prefix="₹"
                  @input="calcPayInsteadOfWeight()"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 sm4 md4 class="pa-2 text-left">
                <div class="field-header-row">
                  <label class="form-label-elegant mb-0">Converted Weight</label>
                </div>
                <v-text-field
                  v-model="payInsteadOfWeight.weightCorrespondingToAmount"
                  outlined
                  dense
                  disabled
                  hide-details
                  class="custom-field disabled-field font-weight-bold"
                  suffix="gm"
                ></v-text-field>
              </v-flex>
            </template>
          </v-layout>
        </div>
      </div>
    </v-card>

    <!-- Sticky Bottom Actions Bar -->
    <div class="bottom-sticky-bar d-flex align-center justify-space-between flex-wrap gap-3 pa-4 px-6 rounded-xl mt-4">
      <div class="d-flex align-center gap-3">
        <div class="voucher-total-pill">
          <span class="total-label">Mode:</span>
          <span class="total-value">{{ settlementMode }}</span>
        </div>
        <div class="voucher-total-pill" v-if="settlementMode === 'Weight' && paidThankam > 0">
          <span class="total-label">Paid Thankam:</span>
          <span class="total-value text-amber-dark">{{ Number(paidThankam).toFixed(3) }} gm</span>
        </div>
        <div class="voucher-total-pill" v-if="settlementMode === 'Cash' && cashPayment.amount > 0">
          <span class="total-label">Paid Cash:</span>
          <span class="total-value text-emerald-dark">₹ {{ formatNumber(netPayableCashAmount) }}</span>
        </div>
      </div>

      <div class="d-flex align-center gap-2">
        <v-btn
          depressed
          class="btn-cancel px-5 font-weight-bold"
          @click="cancel()"
        >
          Cancel
        </v-btn>

        <v-btn
          color="#9a6b27"
          depressed
          dark
          height="42"
          class="btn-save px-6 font-weight-bold"
          @click="saveVoucher()"
        >
          <v-icon left size="18">mdi-content-save-check-outline</v-icon>
          Save Voucher
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "IssueReceipt",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      snackbarColor: "#1e293b",
      timeout: 2500,
      msg: null,
      dateMenu: false,
      invMenu: false,
      voucherNo: "",
      checkVoucherNo: null,
      prefix: "",
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      type: "Issue",
      paymentMode: "Cash",
      supplierId: null,
      supplierList: [],
      invoiceNumber: "",
      nextInvoiceNumber: "", // preview of the auto number the server will assign
      invoiceDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      balanceAmountAtEntry: 0,
      balanceWeightAtEntry: 0,
      itemList: [],
      curItem: {
        selectedItem: null,
        itemCode: "",
        itemName: "",
        quantity: 1,
        category: "Gold",
        grossWeight: 0,
        stoneWeight: 0,
        cent: 0,
        stonePrice: 0,
        touch: 91.6,
        makingCharge: 0,
        taxType: "GST"
      },
      items: [],
      settlementMode: "Weight",
      thankamRate: 0,
      paidThankam: 0,
      payAmount: 0,
      cashPayment: {
        amount: 0,
        cashPayOrCollect: "Pay",
        discount: 0,
        balanceAmount: 0
      },
      payInsteadOfWeight: {
        cashPayOrCollect: "Pay",
        cashTouch: 91.6,
        ratePerGram: 0,
        amount: 0,
        discount: 0,
        weightCorrespondingToAmount: 0
      },
      otherCharges: 0,
      purchaseId: null,
      billNo: null,
      purchaseData: {},
      purchaseItems: [],
      selectedPurchaseItemIds: [],
    };
  },
  computed: {
    unsettledItemsCount() {
      return this.purchaseItems.filter(it => it.settleStatus !== 'Settled').length;
    },
    isAllPurchaseItemsSelected() {
      const unsettled = this.purchaseItems.filter(it => it.settleStatus !== 'Settled');
      return unsettled.length > 0 && this.selectedPurchaseItemIds.length === unsettled.length;
    },
    isPartiallySelected() {
      const unsettled = this.purchaseItems.filter(it => it.settleStatus !== 'Settled');
      return this.selectedPurchaseItemIds.length > 0 && this.selectedPurchaseItemIds.length < unsettled.length;
    },
    selectedThankamWeightTotal() {
      return this.purchaseItems
        .filter(it => this.selectedPurchaseItemIds.includes(it._id))
        .reduce((sum, it) => sum + this.getItemThankamWeight(it), 0);
    },
    itemsTotalNetWeight() {
      return this.items.reduce((sum, it) => sum + ((parseFloat(it.grossWeight) || 0) - (parseFloat(it.stoneWeight) || 0)), 0);
    },
    itemsTotalAmount() {
      return this.items.reduce((sum, it) => sum + (parseFloat(it.itemTotalAmount) || 0), 0);
    },
    requiredCashAmount() {
      const rate = parseFloat(this.thankamRate) || 0;
      const wt = this.selectedThankamWeightTotal || 0;
      return (wt * rate);
    },
    netPayableCashAmount() {
      const amt = parseFloat(this.cashPayment.amount) || 0;
      const disc = parseFloat(this.cashPayment.discount) || 0;
      const other = parseFloat(this.otherCharges) || 0;
      return Math.max(0, (amt - disc + other)).toFixed(2);
    },
  },
  watch: {
    // the auto series depends on voucher type and financial year
    type() {
      this.generateVoucherNo();
    },
    date() {
      this.generateVoucherNo();
    },
    settlementMode(newMode) {
      if (this.purchaseId) {
        if (newMode === "Weight") {
          this.paidThankam = Number(this.selectedThankamWeightTotal || 0).toFixed(3);
        } else if (newMode === "Cash") {
          this.cashPayment.amount = Number(this.requiredCashAmount || 0).toFixed(2);
        } else if (newMode === "PayInsteadOfWeight") {
          this.payInsteadOfWeight.ratePerGram = parseFloat(this.thankamRate) || 0;
          this.payInsteadOfWeight.amount = Number(this.requiredCashAmount || 0).toFixed(2);
          this.calcPayInsteadOfWeight();
        }
      }
    },
    selectedPurchaseItemIds() {
      if (this.purchaseId) {
        if (this.settlementMode === "Weight") {
          this.paidThankam = Number(this.selectedThankamWeightTotal || 0).toFixed(3);
        } else if (this.settlementMode === "Cash") {
          this.cashPayment.amount = Number(this.requiredCashAmount || 0).toFixed(2);
        } else if (this.settlementMode === "PayInsteadOfWeight") {
          this.payInsteadOfWeight.amount = Number(this.requiredCashAmount || 0).toFixed(2);
          this.calcPayInsteadOfWeight();
        }
      }
    },
    thankamRate() {
      if (this.purchaseId) {
        if (this.settlementMode === "Cash") {
          this.cashPayment.amount = Number(this.requiredCashAmount || 0).toFixed(2);
        } else if (this.settlementMode === "PayInsteadOfWeight") {
          this.payInsteadOfWeight.ratePerGram = parseFloat(this.thankamRate) || 0;
          this.payInsteadOfWeight.amount = Number(this.requiredCashAmount || 0).toFixed(2);
          this.calcPayInsteadOfWeight();
        }
      }
    },
  },
  mounted() {
    this.initThankamRate();
    this.getSupplier();
    this.getItemList();
    this.generateVoucherNo();
    if (this.$route.query.prefix && !this.prefix) {
      this.prefix = this.$route.query.prefix;
    }
    if (this.$route.query.purchaseId || this.$route.query.billno) {
      this.purchaseId = this.$route.query.purchaseId;
      this.billNo = this.$route.query.billno;
      this.type = "Issue";
      this.getPurchaseItems();
    }
  },
  methods: {
    initThankamRate() {
      const savedRate = localStorage.getItem("thankamRate");
      if (savedRate && parseFloat(savedRate) > 0) {
        this.thankamRate = parseFloat(savedRate);
        this.payInsteadOfWeight.ratePerGram = parseFloat(savedRate);
      }
    },
    useLiveThankamRate() {
      const savedRate = localStorage.getItem("thankamRate");
      if (savedRate && parseFloat(savedRate) > 0) {
        this.thankamRate = parseFloat(savedRate);
        this.payInsteadOfWeight.ratePerGram = parseFloat(savedRate);
        this.msg = `Live Thankam rate loaded: ₹${this.thankamRate}`;
        this.snackbarColor = "#059669";
        this.showsnackbar = true;
        this.calcPayInsteadOfWeight();
      } else {
        this.msg = "Live rate not available in session";
        this.snackbarColor = "#d97706";
        this.showsnackbar = true;
      }
    },
    formatNumber(num) {
      if (num === null || num === undefined || isNaN(num)) return "0.00";
      return Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    getItemThankamWeight(item) {
      if (!item) return 0;
      if (item.tobePaidThankam && Number(item.tobePaidThankam) > 0) {
        return Number(item.tobePaidThankam);
      }
      if (item.pendingGoldWeight && Number(item.pendingGoldWeight) > 0) {
        return Number(item.pendingGoldWeight);
      }
      if (item.thankam && Number(item.thankam) > 0) {
        return Number(item.thankam);
      }
      const touch = parseFloat(item.touchRate) || parseFloat(item.cost) || 100;
      const net = parseFloat(item.netWeight) || 0;
      return (net * touch) / 100;
    },
    toggleSelectAllPurchaseItems() {
      const unsettled = this.purchaseItems.filter(it => it.settleStatus !== 'Settled');
      if (this.selectedPurchaseItemIds.length === unsettled.length) {
        this.selectedPurchaseItemIds = [];
      } else {
        this.selectedPurchaseItemIds = unsettled.map(it => it._id);
      }
    },
    toggleSingleItem(item) {
      if (item.settleStatus === 'Settled') return;
      const idx = this.selectedPurchaseItemIds.indexOf(item._id);
      if (idx > -1) {
        this.selectedPurchaseItemIds.splice(idx, 1);
      } else {
        this.selectedPurchaseItemIds.push(item._id);
      }
    },
    getItemList() {
      axios({
        method: "get",
        url: "/item/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { count: 1000, page: 1 },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.itemList = response.data.data || [];
          }
        })
        .catch((err) => console.log(err));
    },
    selectItem(item) {
      if (!item) return;
      this.curItem.itemCode = item.code || "";
      this.curItem.itemName = item.name || "";
      if (item.categoryId && item.categoryId.name) {
        const category = String(item.categoryId.name).toLowerCase();
        if (category.includes("diamond")) this.curItem.category = "Diamond";
        else if (category.includes("silver")) this.curItem.category = "Silver";
        else this.curItem.category = "Gold";
      }
    },
    generateVoucherNo() {
      axios({
        method: "post",
        url: "/issuereceipt/bill/generate",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: { type: this.type, date: this.date },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.voucherNo = response.data.voucherNo;
            this.checkVoucherNo = response.data.checkVoucherNo;
            this.nextInvoiceNumber = response.data.nextInvoiceNumber || "";
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getSupplier() {
      axios({
        method: "get",
        url: "/supplier/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.supplierList = response.data.data || [];
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getSupplierBalance() {
      if (!this.supplierId) return;
      axios({
        method: "get",
        url: "/issuereceipt/supplierbalance",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { supplierId: this.supplierId },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.balanceAmountAtEntry = response.data.balanceAmount;
            this.balanceWeightAtEntry = response.data.balanceWeight;
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getPurchaseItems() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/purchase/item/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { purchaseId: this.purchaseId, billNo: this.billNo },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.purchaseItems = (response.data.data || []).map((it) => {
              if (!it.tobePaidThankam || Number(it.tobePaidThankam) === 0) {
                it.tobePaidThankam = this.getItemThankamWeight(it);
              }
              return it;
            });
            this.purchaseData = response.data.purchasedata || {};
            if (this.purchaseData.billPrefix && !this.prefix) {
              this.prefix = this.purchaseData.billPrefix;
            }
            if (this.purchaseData._id) {
              this.purchaseId = this.purchaseData._id;
            }
            // settling a purchase: default to the supplier's invoice on that bill
            if (!this.invoiceNumber && this.purchaseData.invoiceNo) {
              this.invoiceNumber = String(this.purchaseData.invoiceNo);
              if (this.purchaseData.invoiceDate) {
                this.invoiceDate = new Date(this.purchaseData.invoiceDate).toISOString().substr(0, 10);
              }
            }
            if (this.purchaseData.supplierId) {
              this.supplierId = this.purchaseData.supplierId._id;
              this.getSupplierBalance();
            }
            this.selectedPurchaseItemIds = this.purchaseItems
              .filter((it) => it.settleStatus !== "Settled")
              .map((it) => it._id);
            if (this.purchaseData.thankamRate && parseFloat(this.purchaseData.thankamRate) > 0) {
              this.thankamRate = parseFloat(this.purchaseData.thankamRate);
              this.payInsteadOfWeight.ratePerGram = parseFloat(this.purchaseData.thankamRate);
            }
            this.$nextTick(() => {
              if (this.settlementMode === "Weight") {
                this.paidThankam = Number(this.selectedThankamWeightTotal || 0).toFixed(3);
              } else if (this.settlementMode === "Cash") {
                this.cashPayment.amount = Number(this.requiredCashAmount || 0).toFixed(2);
              } else if (this.settlementMode === "PayInsteadOfWeight") {
                this.payInsteadOfWeight.amount = Number(this.requiredCashAmount || 0).toFixed(2);
                this.calcPayInsteadOfWeight();
              }
            });
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    calcPayInsteadOfWeight() {
      const rate = parseFloat(this.payInsteadOfWeight.ratePerGram) || 0;
      const touch = parseFloat(this.payInsteadOfWeight.cashTouch) || 0;
      const amount = parseFloat(this.payInsteadOfWeight.amount) || 0;
      if (rate > 0 && touch > 0) {
        this.payInsteadOfWeight.weightCorrespondingToAmount = (amount / (rate * (touch / 100))).toFixed(3);
      }
    },
    addItem() {
      if (!this.curItem.itemName) {
        this.msg = "Please enter an item name";
        this.snackbarColor = "#ef4444";
        this.showsnackbar = true;
        return;
      }
      const netWeight = (parseFloat(this.curItem.grossWeight) || 0) - (parseFloat(this.curItem.stoneWeight) || 0);
      const goldAmount = netWeight * ((parseFloat(this.curItem.touch) || 0) / 100) * (parseFloat(this.thankamRate) || 0);
      const itemTotalAmount = goldAmount + (parseFloat(this.curItem.makingCharge) || 0) + (parseFloat(this.curItem.stonePrice) || 0);
      
      this.items.push({
        itemCode: this.curItem.itemCode,
        itemName: this.curItem.itemName,
        quantity: parseFloat(this.curItem.quantity) || 0,
        category: this.curItem.category,
        grossWeight: parseFloat(this.curItem.grossWeight) || 0,
        stoneWeight: parseFloat(this.curItem.stoneWeight) || 0,
        cent: parseFloat(this.curItem.cent) || 0,
        stonePrice: parseFloat(this.curItem.stonePrice) || 0,
        touch: parseFloat(this.curItem.touch) || 0,
        makingCharge: parseFloat(this.curItem.makingCharge) || 0,
        taxType: this.curItem.taxType,
        itemTotalAmount: itemTotalAmount,
      });

      this.curItem = {
        selectedItem: null,
        itemCode: "",
        itemName: "",
        quantity: 1,
        category: "Gold",
        grossWeight: 0,
        stoneWeight: 0,
        cent: 0,
        stonePrice: 0,
        touch: 91.6,
        makingCharge: 0,
        taxType: "GST"
      };

      this.msg = "Item added to voucher";
      this.snackbarColor = "#059669";
      this.showsnackbar = true;
    },
    cancel() {
      this.$router.go(-1);
    },
    saveVoucher() {
      if (!this.supplierId) {
        this.msg = "Please select a supplier";
        this.snackbarColor = "#ef4444";
        this.showsnackbar = true;
        return;
      }
      if (!this.purchaseId && this.items.length === 0) {
        this.msg = "Please add at least one item";
        this.snackbarColor = "#ef4444";
        this.showsnackbar = true;
        return;
      }
      if (this.purchaseId && this.selectedPurchaseItemIds.length === 0) {
        this.msg = "Please select at least one item to settle";
        this.snackbarColor = "#ef4444";
        this.showsnackbar = true;
        return;
      }

      let totalThankamWeight = 0;
      if (this.purchaseId) {
        totalThankamWeight = this.selectedThankamWeightTotal;
      }

      const rate = parseFloat(this.thankamRate) || 0;
      let paidThankam = 0;
      let payAmount = 0;

      if (this.settlementMode === "Weight") {
        paidThankam = parseFloat(this.paidThankam) || 0;
        payAmount = 0;
      } else if (this.settlementMode === "Cash") {
        payAmount = parseFloat(this.cashPayment.amount) || 0;
        paidThankam = rate > 0 ? (payAmount / rate) : totalThankamWeight;
      } else if (this.settlementMode === "PayInsteadOfWeight") {
        payAmount = parseFloat(this.payInsteadOfWeight.amount) || 0;
        paidThankam = parseFloat(this.payInsteadOfWeight.weightCorrespondingToAmount) || (rate > 0 ? payAmount / rate : 0);
      }

      this.appLoading = true;
      axios({
        method: "post",
        url: "/issuereceipt/add",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: {
          prefix: this.prefix,
          voucherNo: this.voucherNo,
          checkVoucherNo: this.checkVoucherNo,
          date: this.date,
          type: this.type,
          supplierId: this.supplierId,
          invoiceNumber: this.invoiceNumber,
          invoiceDate: this.invoiceDate,
          paymentMode: this.paymentMode,
          settlementMode: this.settlementMode,
          thankamRate: this.thankamRate,
          otherCharges: this.otherCharges,
          cashPayment: this.settlementMode === "Cash" ? this.cashPayment : undefined,
          payInsteadOfWeight: this.settlementMode === "PayInsteadOfWeight" ? this.payInsteadOfWeight : undefined,
          items: this.items,
          purchaseId: this.purchaseId,
          currentpurchaseItems: this.selectedPurchaseItemIds,
          totalThankamWeight: totalThankamWeight,
          paidThankam: paidThankam,
          payAmount: payAmount,
          // paymentType drives the underlying purchase-settlement ledger entry
          // (Cash vs Thankam) - it must reflect the chosen Settlement Method,
          // not the unrelated Cash/Bank ledger selector (this.paymentMode),
          // which always stays "Cash" unless the user is doing a plain Cash
          // settlement and happens to also touch that field.
          paymentType: this.settlementMode === "Cash" ? "Cash" : "Thankam",
          useOwnThankam: this.settlementMode === "Weight",
          ownThankamAmount: this.settlementMode === "Weight" ? paidThankam : 0,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = "Voucher saved successfully";
            this.snackbarColor = "#059669";
            this.showsnackbar = true;
            this.$router.push("/IssueReceiptList");
          } else {
            this.msg = response.data.msg || "Failed to save voucher";
            this.snackbarColor = "#ef4444";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
.invoice-auto-tag {
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #15803d;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 4px;
  padding: 0 5px;
}
.invoice-hint {
  margin-top: 4px;
  font-size: 11px;
  color: #94a3b8;
}
.ir-modern-page {
  max-width: 1440px;
  margin: 0 auto;
  padding: 4px 0 80px 0;
  color: #0f172a;
}

/* Header & Breadcrumbs */
.breadcrumbs-sub {
  font-size: 12.5px;
}

.breadcrumb-link {
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.15s ease;
}

.breadcrumb-link:hover {
  color: #9a6b27;
}

.breadcrumb-current {
  color: #0f172a;
  font-weight: 600;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.02em;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.back-btn {
  background-color: #f1f5f9 !important;
  border-radius: 10px !important;
}

.voucher-badge {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
  background-color: #fef3c7;
  border: 1px solid #fde68a;
  padding: 3px 9px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
}

.type-pill {
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.type-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.type-issue {
  background-color: #fff7ed;
  color: #c2410c;
  border: 1px solid #ffedd5;
}

.type-issue .type-dot {
  background-color: #ea580c;
}

.type-receipt {
  background-color: #f0fdf4;
  color: #15803d;
  border: 1px solid #dcfce7;
}

.type-receipt .type-dot {
  background-color: #16a34a;
}

.payment-mode-pill {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 3px 9px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
}

.action-outline-btn {
  border-color: #e2e8f0 !important;
  color: #475569 !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
  border-radius: 8px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  background-color: #ffffff !important;
}

.action-outline-btn:hover {
  background-color: #f8fafc !important;
  border-color: #cbd5e1 !important;
  color: #0f172a !important;
}

/* Section Cards */
.section-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.03) !important;
  overflow: hidden;
}

.section-header {
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
}

.header-icon-circle {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}

.section-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

/* Form Inputs */
.field-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
  margin-bottom: 6px;
}

.form-label-elegant {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 0;
  display: inline-block;
  white-space: nowrap;
}

.required-star {
  color: #ef4444;
}

.custom-field ::v-deep .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  font-size: 13.5px !important;
  min-height: 40px !important;
  height: 40px !important;
}

.custom-field ::v-deep .v-input__append-inner {
  margin-top: 8px !important;
}

.custom-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.custom-field ::v-deep fieldset {
  border-color: #e2e8f0 !important;
  transition: border-color 0.15s ease;
}

.custom-field:hover ::v-deep fieldset {
  border-color: #cbd5e1 !important;
}

.custom-field.v-input--is-focused ::v-deep fieldset {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.disabled-field ::v-deep .v-input__slot {
  background-color: #f8fafc !important;
}

.disabled-field ::v-deep input {
  color: #475569 !important;
  font-weight: 600 !important;
}

/* Balance Widgets */
.balance-widgets-container {
  display: flex;
  gap: 8px;
  height: 40px;
  align-items: center;
}

.balance-widget {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px 10px;
}

.balance-widget-icon {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.balance-widget-label {
  font-size: 10.5px;
  font-weight: 600;
  color: #64748b;
  display: block;
  line-height: 1.1;
}

.balance-widget-value {
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.2;
}

/* Builder Form Grid */
.builder-grid-container {
  background-color: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
}

.btn-primary-gradient {
  background: linear-gradient(135deg, #c46816 0%, #b85b0d 100%) !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-size: 13px !important;
  letter-spacing: 0.2px !important;
}

/* Tables */
.modern-table-wrapper {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table thead tr th {
  background-color: #f8fafc;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 11px 14px;
  border-bottom: 1px solid #e2e8f0;
}

.modern-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.12s ease;
}

.modern-table tbody tr:hover {
  background-color: #fafaf9;
}

.modern-table tbody tr td {
  padding: 10px 14px;
  font-size: 13px;
  color: #1e293b;
}

.row-selected {
  background-color: #fffbeb !important;
}

.item-avatar-circle {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.code-pill {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11.5px;
  font-weight: 600;
  color: #334155;
  background-color: #f1f5f9;
  padding: 2px 6px;
  border-radius: 5px;
  border: 1px solid #e2e8f0;
}

.category-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 5px;
}

.cat-gold {
  background-color: #fffbeb;
  color: #b45309;
  border: 1px solid #fef3c7;
}

.cat-silver {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.cat-diamond {
  background-color: #eff6ff;
  color: #2563eb;
  border: 1px solid #dbeafe;
}

.status-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.status-settled {
  background-color: #f0fdf4;
  color: #15803d;
  border: 1px solid #dcfce7;
}

.status-unsettled {
  background-color: #fffbeb;
  color: #b45309;
  border: 1px solid #fef3c7;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.dot-settled { background-color: #16a34a; }
.dot-unsettled { background-color: #f59e0b; }

.table-summary-strip {
  background-color: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.summary-stat-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 10px;
}

.summary-stat-label {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
}

.summary-stat-val {
  font-size: 13.5px;
  font-weight: 700;
}

.badge-emerald-soft {
  background-color: #ecfdf5 !important;
  border-color: #a7f3d0 !important;
}

.text-emerald-dark {
  color: #047857 !important;
}

.fill-full-tag {
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 1px 8px;
  border-radius: 12px;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  user-select: none;
  line-height: 1.3;
  white-space: nowrap;
}

.fill-full-tag:hover {
  transform: translateY(-1px);
}

.fill-full-tag.text-amber {
  color: #b45309 !important;
  background-color: #fffbeb;
  border-color: #fde68a;
}

.fill-full-tag.text-amber:hover {
  background-color: #fef3c7;
}

.fill-full-tag.text-emerald {
  color: #047857 !important;
  background-color: #ecfdf5;
  border-color: #a7f3d0;
}

.fill-full-tag.text-emerald:hover {
  background-color: #d1fae5;
  border-color: #6ee7b7;
}

/* Settlement Mode Selector Cards */
.settlement-modes-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}

.settlement-mode-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background-color: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.settlement-mode-card:hover {
  border-color: #cbd5e1;
  background-color: #fafaf9;
}

.settlement-mode-card.mode-active {
  border-color: #9a6b27;
  background-color: #fffdfa;
  box-shadow: 0 4px 12px rgba(184, 106, 36, 0.08);
}

.mode-radio-circle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.mode-active .mode-radio-circle {
  background-color: #9a6b27;
  border-color: #9a6b27;
}

.mode-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mode-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.mode-desc {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.2;
}

.settlement-fields-box {
  background-color: #f8fafc;
  border: 1px solid #f1f5f9;
}

.live-rate-tag {
  font-size: 11px;
  font-weight: 600;
  color: #9a6b27;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  background-color: #fff7ed;
  border-radius: 4px;
}

.live-rate-tag:hover {
  background-color: #fed7aa;
}

/* Action Icon Buttons */
.action-icon-btn {
  border-radius: 6px !important;
  transition: all 0.15s ease !important;
}

.delete-action-btn:hover {
  background-color: #fef2f2 !important;
}

/* Empty State */
.empty-state-card {
  background-color: #fafaf9;
}

.empty-icon-circle {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background-color: #f1f5f9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.empty-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

/* Bottom Sticky / Summary Bar */
.bottom-sticky-bar {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05);
  margin-bottom: 24px;
}

.voucher-total-pill {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.total-label {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
}

.total-value {
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
}

.btn-cancel {
  background-color: #f1f5f9 !important;
  color: #475569 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-size: 13px !important;
}

.btn-cancel:hover {
  background-color: #e2e8f0 !important;
  color: #0f172a !important;
}

.btn-save {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-size: 13.5px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.btn-save:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px) !important;
}

/* Utility Helpers */
.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

.bg-amber-soft { background-color: #fff7ed; }
.bg-emerald-soft { background-color: #f0fdf4; }
.bg-blue-soft { background-color: #eff6ff; }

.text-amber-dark { color: #b45309 !important; }
.text-emerald-dark { color: #15803d !important; }
.text-danger { color: #dc2626 !important; }
.text-dark { color: #0f172a !important; }
.text-muted { color: #64748b !important; }
.unit { font-size: 11.5px; font-weight: 500; color: #94a3b8; }

.bill-badge {
  font-size: 11.5px;
  font-weight: 700;
  color: #1d4ed8;
  background-color: #eff6ff;
  border: 1px solid #dbeafe;
  padding: 2px 7px;
  border-radius: 5px;
}

/* ---- Phones ---- */
@media (max-width: 600px) {
  /* label + "Req: ..." chip: wrap instead of overflowing the field */
  .field-header-row {
    flex-wrap: wrap;
    gap: 4px 8px;
    justify-content: flex-start;
  }
  .fill-full-tag {
    white-space: normal;
    max-width: 100%;
  }
  /* bottom summary bar: pills wrap, buttons share a full-width row */
  .bottom-sticky-bar {
    padding: 12px !important;
  }
  .bottom-sticky-bar > div {
    width: 100%;
    flex-wrap: wrap;
    gap: 8px;
  }
  .voucher-total-pill {
    flex: 1 1 auto;
    flex-wrap: wrap;
    min-width: 0;
  }
  .bottom-sticky-bar .btn-cancel,
  .bottom-sticky-bar .btn-save {
    flex: 1 1 0;
    min-width: 0;
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
}
</style>
